import axios from "axios";
import { store } from "../redux/index";
import { TOKEN_REFRESHED } from "../redux/actions/authActions";

const apiBaseUrl =`${import.meta.env.VITE_APP_ASSAD_BASE_API_URL}`;

const withAuthInstance = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshTokenPromise = null;

// Backend returns 403 ("No token provided!") for missing/expired tokens in
// addition to 401, so treat both as auth failures that should trigger /refresh.
const isAuthError = (status) => status === 401 || status === 403;

const forceFullRelogin = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("web3-token");

  if (typeof window !== "undefined") {
    window.location.assign("/");
  }
};

const extractAccessToken = (data) => {
  return data?.accessToken || data?.token || data?.data?.accessToken || null;
};

const refreshAccessToken = async (oldToken) => {
  // The token refresh endpoint lives on the main API host root, not under /assad/.
  const response = await withAuthInstance.post(
    `${import.meta.env.VITE_APP_BASE_URL}refresh`,
    {},
    {
      headers: {
        "x-access-token": oldToken,
      },
      skipAuthRefresh: true,
    }
  );

  const nextToken = extractAccessToken(response?.data);
  if (!nextToken) {
    throw new Error("Refresh response did not include a new access token");
  }

  localStorage.setItem("token", nextToken);
  store.dispatch({ type: TOKEN_REFRESHED, token: nextToken });
  return nextToken;
};

withAuthInstance.interceptors.request.use(
  (config) => {
    const token =
    localStorage.getItem("token") 

    const hasExplicitToken = Boolean(config?.headers?.["x-access-token"]);

    if (token && !hasExplicitToken) {
      //config.headers.Authorization = token;
      config.headers["x-access-token"] = token;
    } else if (!token) {
      //localStorage.clear(); // use this if needed. this will remove all items from localstorage
      delete withAuthInstance.defaults.headers.common.Authorization;
    }
    return config;
  },

  (error) => console.error(error)
);

withAuthInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const requestUrl = String(originalRequest?.url || "");
    const isRefreshRequest = requestUrl.includes("/refresh");

    if (!originalRequest || originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    if (isRefreshRequest && isAuthError(status)) {
      forceFullRelogin();
      return Promise.reject(error);
    }

    if (isAuthError(status) && originalRequest?._retryAfterRefresh) {
      forceFullRelogin();
      return Promise.reject(error);
    }

    if (!isAuthError(status)) {
      return Promise.reject(error);
    }

    try {
      if (!refreshTokenPromise) {
        const oldToken = localStorage.getItem("token");

        if (!oldToken) {
          forceFullRelogin();
          return Promise.reject(error);
        }

        refreshTokenPromise = refreshAccessToken(oldToken).finally(() => {
          refreshTokenPromise = null;
        });
      }

      const refreshedToken = await refreshTokenPromise;

      originalRequest._retryAfterRefresh = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers["x-access-token"] = refreshedToken;

      return withAuthInstance(originalRequest);
    } catch (refreshError) {
      if (isAuthError(refreshError?.response?.status)) {
        forceFullRelogin();
      }

      return Promise.reject(refreshError);
    }
  }
);

export default withAuthInstance;
